import { useState, useEffect } from "react";
import {
  LayoutDashboard, UtensilsCrossed, ShoppingBasket,
  Package, Bike, Users, BadgePercent, Bell,
  Settings, ChevronLeft, ChevronRight, Menu, X,
  LogOut, Zap, TrendingUp, ClipboardList, Store, Image,
  Wallet,
} from "lucide-react";

import Login                from "./pages/Login";
import Dashboard            from "./pages/Dashboard";
import Restaurants          from "./pages/Restaurants";
import MenuItems            from "./pages/MenuItems";
import GroceryStores        from "./pages/GroceryStores";
import Orders               from "./pages/Orders";
import Courier              from "./pages/Courier";
import Riders               from "./pages/Riders";
import UsersPage            from "./pages/UsersPage";
import Coupons              from "./pages/Coupons";
import NotificationsPage    from "./pages/NotificationsPage";
import SettingsPage         from "./pages/SettingsPage";
import VendorApplications   from "./pages/VendorApplications";
import VendorOrders         from "./pages/VendorOrders";
import Banners              from "./pages/Banners";
import AdminWallet          from "./pages/AdminWallet";   // ✅ NEW

const T = {
  primary:"#f97316", primaryDark:"#ea580c", primaryLight:"#fff7ed", primaryMid:"#fed7aa",
  sidebar:"#0f172a", sidebarHover:"#1e293b", sidebarActive:"#1e293b",
  white:"#ffffff", bg:"#f1f5f9", text:"#0f172a", textSub:"#64748b", textMuted:"#94a3b8",
  border:"#e2e8f0", green:"#16a34a", red:"#ef4444", yellow:"#f59e0b", blue:"#2563eb",
};

const NAV = [
  { group:"OVERVIEW", items:[
    { id:"dashboard",     label:"Dashboard",           icon:LayoutDashboard, badge:null  },
  ]},
  { group:"SERVICES", items:[
    { id:"restaurants",   label:"Restaurants",         icon:UtensilsCrossed, badge:null  },
    { id:"menu",          label:"Menu Items",          icon:ClipboardList,   badge:null  },
    { id:"grocery",       label:"Grocery Stores",      icon:ShoppingBasket,  badge:null  },
    { id:"courier",       label:"Courier / Pickup",    icon:Package,         badge:"New" },
  ]},
  { group:"OPERATIONS", items:[
    { id:"orders",        label:"All Orders",          icon:TrendingUp,      badge:null  },
    { id:"vendor-orders", label:"Vendor Orders",       icon:ClipboardList,   badge:"Live"},
    { id:"riders",        label:"Delivery Riders",     icon:Bike,            badge:null  },
    { id:"users",         label:"Customers",           icon:Users,           badge:null  },
    { id:"vendors",       label:"Vendor Applications", icon:Store,           badge:"New" },
  ]},
  { group:"PAYMENTS", items:[                                                // ✅ NEW GROUP
    { id:"wallet",        label:"Wallet Requests",     icon:Wallet,          badge:"New" },
  ]},
  { group:"MARKETING", items:[
    { id:"coupons",       label:"Offers & Coupons",    icon:BadgePercent,    badge:null  },
    { id:"banners",       label:"Hero Banners",        icon:Image,           badge:null  },
    { id:"notifications", label:"Notifications",       icon:Bell,            badge:null  },
  ]},
  { group:"SYSTEM", items:[
    { id:"settings",      label:"Settings",            icon:Settings,        badge:null  },
  ]},
];

function Sidebar({ active, onNav, collapsed, onToggle, isMobile, showMobile, onCloseMobile, onLogout, adminEmail, pendingWallet }) {
  return (
    <>
      {isMobile && showMobile && (
        <div onClick={onCloseMobile} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200 }}/>
      )}
      <aside style={{ position:isMobile?"fixed":"relative", left:isMobile?(showMobile?0:-280):0, top:0, bottom:0, width:collapsed?72:256, background:T.sidebar, display:"flex", flexDirection:"column", transition:"width .25s ease, left .25s ease", zIndex:isMobile?300:1, flexShrink:0, overflowX:"hidden", boxShadow:isMobile?"4px 0 24px rgba(0,0,0,0.3)":"none" }}>

        <div style={{ padding:collapsed?"20px 0":"20px 20px", display:"flex", alignItems:"center", justifyContent:collapsed?"center":"space-between", borderBottom:"1px solid rgba(255,255,255,0.07)", minHeight:72 }}>
          {!collapsed && (
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              <div style={{ width:36,height:36,borderRadius:10,background:T.primary,display:"flex",alignItems:"center",justifyContent:"center" }}>
                <Zap size={20} color="#fff" fill="#fff"/>
              </div>
              <div>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1.15rem",letterSpacing:"-0.5px",color:"#fff" }}>Quick<span style={{ color:T.primary }}>ry</span></div>
                <div style={{ fontSize:"0.6rem",color:"rgba(255,255,255,0.4)",fontWeight:600,letterSpacing:"1.5px" }}>ADMIN PANEL</div>
              </div>
            </div>
          )}
          {collapsed && <div style={{ width:36,height:36,borderRadius:10,background:T.primary,display:"flex",alignItems:"center",justifyContent:"center" }}><Zap size={20} color="#fff" fill="#fff"/></div>}
          {!isMobile && (
            <button onClick={onToggle} style={{ background:"rgba(255,255,255,0.07)",border:"none",borderRadius:8,width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"rgba(255,255,255,0.5)",flexShrink:0 }}>
              {collapsed?<ChevronRight size={14}/>:<ChevronLeft size={14}/>}
            </button>
          )}
          {isMobile && <button onClick={onCloseMobile} style={{ background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.5)" }}><X size={18}/></button>}
        </div>

        <nav style={{ flex:1,overflowY:"auto",overflowX:"hidden",padding:"12px 0",scrollbarWidth:"none" }}>
          {NAV.map(group => (
            <div key={group.group} style={{ marginBottom:8 }}>
              {!collapsed && <div style={{ padding:"8px 20px 4px",fontSize:"0.58rem",fontWeight:700,color:"rgba(255,255,255,0.25)",letterSpacing:"1.5px" }}>{group.group}</div>}
              {collapsed && <div style={{ height:8 }}/>}
              {group.items.map(item => {
                const Icon     = item.icon;
                const isActive = active === item.id;

                // ✅ Wallet pending count badge
                const badgeLabel = item.id === "wallet" && pendingWallet > 0
                  ? String(pendingWallet)
                  : item.badge;
                const badgeBg = item.id === "wallet" && pendingWallet > 0
                  ? T.red
                  : item.badge === "New" ? T.green : T.primary;

                return (
                  <button key={item.id} onClick={()=>{ onNav(item.id); if(isMobile) onCloseMobile(); }}
                    style={{ width:"100%",background:isActive?"rgba(249,115,22,0.15)":"none",border:"none",borderLeft:`3px solid ${isActive?T.primary:"transparent"}`,display:"flex",alignItems:"center",gap:12,padding:collapsed?"11px 0":"11px 17px",justifyContent:collapsed?"center":"flex-start",cursor:"pointer",transition:"all .15s",color:isActive?T.primary:"rgba(255,255,255,0.6)" }}
                    onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background="rgba(255,255,255,0.05)";}}
                    onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="none";}}>
                    <div style={{ position:"relative", flexShrink:0 }}>
                      <Icon size={18} strokeWidth={isActive?2.2:1.8}/>
                      {/* ✅ Dot badge for collapsed mode */}
                      {collapsed && item.id === "wallet" && pendingWallet > 0 && (
                        <div style={{ position:"absolute", top:-4, right:-4, width:8, height:8, borderRadius:"50%", background:T.red, border:"1.5px solid " + T.sidebar }}/>
                      )}
                    </div>
                    {!collapsed && (
                      <>
                        <span style={{ fontSize:"0.85rem",fontWeight:isActive?700:500,flex:1,textAlign:"left",whiteSpace:"nowrap" }}>{item.label}</span>
                        {badgeLabel && (
                          <span style={{ background:badgeBg, color:"#fff", fontSize:"0.6rem", fontWeight:800, padding:"2px 7px", borderRadius:100, minWidth:20, textAlign:"center" }}>
                            {badgeLabel}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)",padding:collapsed?"14px 0":"14px 16px" }}>
          {!collapsed ? (
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              <div style={{ width:36,height:36,borderRadius:10,background:T.primary,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                <span style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,color:"#fff",fontSize:"1rem" }}>A</span>
              </div>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontSize:"0.82rem",fontWeight:700,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>Admin</div>
                <div style={{ fontSize:"0.68rem",color:"rgba(255,255,255,0.4)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{adminEmail||"Quickry Akola"}</div>
              </div>
              <button onClick={onLogout} title="Logout"
                style={{ background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.4)",display:"flex",padding:4,borderRadius:8 }}
                onMouseEnter={e=>e.currentTarget.style.color=T.red}
                onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,0.4)"}>
                <LogOut size={16}/>
              </button>
            </div>
          ) : (
            <div style={{ display:"flex",justifyContent:"center" }}>
              <button onClick={onLogout} style={{ background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.4)",display:"flex" }}><LogOut size={18}/></button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function Topbar({ page, onMenuClick, isMobile, pendingWallet, onNav }) {
  const pageLabel = NAV.flatMap(g=>g.items).find(i=>i.id===page)?.label || "Dashboard";
  return (
    <header style={{ height:64,background:T.white,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",padding:"0 24px",gap:14,position:"sticky",top:0,zIndex:50,boxShadow:"0 1px 8px rgba(0,0,0,0.04)" }}>
      {isMobile && <button onClick={onMenuClick} style={{ background:"none",border:"none",cursor:"pointer",color:T.text,display:"flex",padding:4 }}><Menu size={22}/></button>}
      <div style={{ flex:1 }}>
        <div style={{ fontFamily:"'Fraunces',Georgia,serif",fontWeight:900,fontSize:"1.1rem",letterSpacing:"-0.5px",color:T.text }}>{pageLabel}</div>
        <div style={{ fontSize:"0.68rem",color:T.textMuted }}>Quickry Admin · Akola</div>
      </div>

      {/* ✅ Wallet pending alert in topbar */}
      {pendingWallet > 0 && (
        <button onClick={() => onNav("wallet")}
          style={{ display:"flex", alignItems:"center", gap:7, background:"#fef3c7", border:"1px solid #fde68a", borderRadius:10, padding:"7px 13px", cursor:"pointer", color:"#92400e", fontWeight:700, fontSize:"0.78rem" }}>
          <Wallet size={14} color="#d97706"/>
          {pendingWallet} Pending
        </button>
      )}

      <button style={{ position:"relative",background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,width:38,height:38,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer" }}>
        <Bell size={17} color={T.textSub}/>
        <div style={{ position:"absolute",top:8,right:8,width:8,height:8,borderRadius:"50%",background:T.red,border:"2px solid #fff" }}/>
      </button>
    </header>
  );
}

function renderPage(page) {
  switch(page) {
    case "dashboard":     return <Dashboard />;
    case "restaurants":   return <Restaurants />;
    case "menu":          return <MenuItems />;
    case "grocery":       return <GroceryStores />;
    case "orders":        return <Orders />;
    case "vendor-orders": return <VendorOrders />;
    case "courier":       return <Courier />;
    case "riders":        return <Riders />;
    case "users":         return <UsersPage />;
    case "vendors":       return <VendorApplications />;
    case "coupons":       return <Coupons />;
    case "banners":       return <Banners />;
    case "notifications": return <NotificationsPage />;
    case "settings":      return <SettingsPage />;
    case "wallet":        return <AdminWallet />;   // ✅ NEW
    default:              return <Dashboard />;
  }
}

export default function App() {
  const [page,          setPage]          = useState("dashboard");
  const [collapsed,     setCollapsed]     = useState(false);
  const [showMobile,    setShowMobile]    = useState(false);
  const [isLoggedIn,    setIsLoggedIn]    = useState(false);
  const [adminEmail,    setAdminEmail]    = useState("");
  const [checking,      setChecking]      = useState(true);
  const [pendingWallet, setPendingWallet] = useState(0);  // ✅ pending count

  useEffect(() => {
    const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
    if (token) {
      const data = JSON.parse(localStorage.getItem("adminData") || "{}");
      setAdminEmail(data.email || "");
      setIsLoggedIn(true);
    }
    setChecking(false);
  }, []);

  // ✅ Fetch pending wallet count every 60 seconds
  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchPendingCount = async () => {
      try {
        const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
        const res   = await fetch(
          (window.location.hostname !== "localhost" ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api") + "/wallet/admin/pending",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (data.success) setPendingWallet(data.requests?.length || 0);
      } catch {}
    };
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000); // every 60s
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    localStorage.removeItem("token");
    setIsLoggedIn(false);
    setPage("dashboard");
  };

  const handleLoginSuccess = () => {
    const data = JSON.parse(localStorage.getItem("adminData") || "{}");
    setAdminEmail(data.email || "");
    setIsLoggedIn(true);
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  if (checking) return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#0f172a" }}>
      <div style={{ color:"#f97316",fontSize:"1.5rem",fontWeight:700 }}>Loading...</div>
    </div>
  );

  if (!isLoggedIn) return <Login onSuccess={handleLoginSuccess}/>;

  return (
    <div style={{ display:"flex",height:"100vh",fontFamily:"'Plus Jakarta Sans','Outfit',sans-serif",background:T.bg,overflow:"hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:5px;height:5px;}
        ::-webkit-scrollbar-track{background:transparent;}
        ::-webkit-scrollbar-thumb{background:#e2e8f0;border-radius:10px;}
        body{overflow:hidden;}
      `}</style>
      <Sidebar
        active={page} onNav={setPage}
        collapsed={collapsed} onToggle={()=>setCollapsed(c=>!c)}
        isMobile={isMobile} showMobile={showMobile}
        onCloseMobile={()=>setShowMobile(false)}
        onLogout={handleLogout} adminEmail={adminEmail}
        pendingWallet={pendingWallet}
      />
      <div style={{ flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0 }}>
        <Topbar
          page={page} onMenuClick={()=>setShowMobile(true)}
          isMobile={isMobile}
          pendingWallet={pendingWallet}
          onNav={setPage}
        />
        <main style={{ flex:1,overflowY:"auto",padding:"24px" }}>
          {renderPage(page)}
        </main>
      </div>
    </div>
  );
}